//* Vue
import type { SetupContext } from "vue";
import { nextTick, computed, reactive, inject, ref, watch } from "vue";
//* 工具函数
import { node, dispose, utility, animations } from "@various/utils";
//* 公共属性
import { UiFormEmitterKey } from "@various/constants";
//* 组件属性
import type { UiDateRangePickerProps, UiDateRangePickerEmits } from "./component";
import { analyzes } from "./utils";
//* 组件引入
import PickerTime from "../components/time/index.vue";
import PickerDate from "../components/date/index.vue";
import PickerMonth from "../components/month/index.vue";

type ComponentNodeType = InstanceType<typeof PickerDate> | InstanceType<typeof PickerTime> | InstanceType<typeof PickerMonth>;

export const useComposable = (define: UiDateRangePickerProps, emits: SetupContext<typeof UiDateRangePickerEmits>["emit"]) => {
    //* 初始化mitt
    const emitter = inject(UiFormEmitterKey, undefined);

    //* 静态变量
    const vars = {
        observer: <ResizeObserver | undefined>undefined,
    };

    //* 响应式变量
    const refs = {
        end: ref<Date | undefined>(analyzes.date(define.modelValue.end)),
        date: ref<Date>(analyzes.date(define.modelValue.start) || analyzes.date(define.modelValue.end) || new Date()),
        mode: ref<string>(define.mode), //* 窗口类型
        start: ref<Date | undefined>(analyzes.date(define.modelValue.start)),
        visible: ref<boolean>(false),
    };

    //* 节点列表
    const nodes = {
        container: ref<HTMLElement>(), //* 时间选择器节点
        candidates: ref<HTMLElement>(), //* 候选项容器节点
        components: ref<ComponentNodeType>(), //* 候选项组件节点
    };

    //* 计算属性
    const computeds = {
        //* 组件类
        className: computed(() => (refs.visible.value ? "ui-active" : "")),
        view: computed(() => {
            if (refs.mode.value == "month") return PickerMonth;
            else if (refs.mode.value == "time") return PickerTime;
            else {
                return PickerDate;
            }
        }),
    };

    //* 函数列表
    const methods = {
        //* 显示函数
        show: (ev: MouseEvent) => {
            //* 检测窗口是否已开启
            if (refs.visible.value) {
                if (refs.mode.value != "time") return methods.hidden();
                else {
                    return nodes.components.value?.init({ end: refs.end.value, date: refs.date.value, start: refs.start.value, event: ev });
                }
            } else {
                //* 更新控制变量
                refs.visible.value = true;
                refs.mode.value = define.mode;
                //* 下一帧初始化组件
                nextTick(() => {
                    //* 组件内容初始化
                    nodes.components.value?.init({ end: define.modelValue.end, date: refs.date.value, start: define.modelValue.start, event: ev });
                    //* 下一帧进行候选窗口的定位与时间挂载
                    nextTick(() => {
                        //* 检测是否满足运行条件
                        if (!nodes.container.value || !nodes.candidates.value) return;
                        else {
                            //* 将内容添加到视图容器中
                            node.append(document.body, nodes.candidates.value);
                            //* 隐藏事件
                            window.addEventListener("click", methods.hidden, { capture: true, once: true });
                            //* 注册观察者
                            vars.observer?.disconnect();
                            vars.observer = new ResizeObserver(() => {
                                if (!nodes.container.value || !nodes.candidates.value) return;
                                else {
                                    //* 数据初始化
                                    const data = { container: nodes.container.value, view: nodes.candidates.value };
                                    dispose.boundary.relativeContainerBody(data, {
                                        direction: "bottom",
                                        offset: 8,
                                        align: "center",
                                    });
                                }
                            });

                            vars.observer.observe(document.documentElement);
                        }
                    });
                });
            }
        },

        //* 隐藏函数
        hidden: (ev?: Event): void => {
            //* 检测是否满足运行条件
            if (!nodes.container.value || !nodes.candidates.value) return;
            else {
                //* 初始化数据
                const target = ev?.target as HTMLElement;
                //* 检测是否需要进行关闭候选菜单
                if (target && (node.includes(target, nodes.container.value) || node.includes(target, nodes.candidates.value))) {
                    return window.addEventListener("click", methods.hidden, { capture: true, once: true });
                } else {
                    //* 状态更新
                    refs.visible.value = false;
                    refs.start.value = analyzes.date(define.modelValue.start);
                    refs.end.value = analyzes.date(define.modelValue.end);
                    //* 注销观察者
                    vars.observer?.disconnect();
                    //* 表单校验响应
                    emitter?.emit(define.name, "blur");
                }
            }
        },
    };

    //* 侦听器列表
    const watchs = {
        stop_watch: watch(
            () => define.modelValue,
            () => {
                refs.end.value = analyzes.date(define.modelValue.end);
                refs.start.value = analyzes.date(define.modelValue.start);
                refs.date.value = analyzes.date(define.modelValue.start) || analyzes.date(define.modelValue.end) || new Date();
            }
        ),
    };

    //* 属性列表
    const binds = reactive({
        end: computed(() => {
            return {
                placeholder: define.placeholder.end,
                value: analyzes.string(define.mode, refs.end.value),
            };
        }),

        start: computed(() => {
            return {
                placeholder: define.placeholder.start,
                value: analyzes.string(define.mode, refs.start.value),
            };
        }),

        //* 容器
        container: computed(() => {
            return {
                class: computeds.className.value,
                style: {
                    width: utility.isNumber(define.width) ? define.width + "px" : define.width,
                },
            };
        }),

        //* 候选项容器
        candidates: computed(() => {
            return {
                class: define.classExtraName || "",
                style: {
                    zIndex: define.zIndex,
                },
            };
        }),
    });

    //* 响应事件
    const ons = {
        //* 候选项
        candidates: animations.selector(true, {
            beforeEnter: () => emits("before-enter"),
            beforeLeave: () => emits("before-leave"),
            afterEnter: () => emits("after-enter"),
            afterLeave: () => emits("after-leave"),
        }),

        //* 组件
        components: {
            //* 数据更新
            change: (data: { start?: Date; end?: Date }) => {
                //* 数值更新
                refs.end.value = data.end;
                refs.start.value = data.start;
                emits("update:modelValue", data);
                emitter?.emit(define.name, "change");
                nextTick(() => {
                    if (define.modelValue.start && define.modelValue.end) {
                        methods.hidden();
                    }
                });
            },

            //* 状态更新
            update: (data: { year?: number; month?: number }) => {
                //* 数据更新
                if (data.year != undefined) refs.date.value.setFullYear(data.year);
                if (data.month != undefined) {
                    refs.date.value.setMonth(data.month);
                }

                //* 组件状态更新
                nodes.components.value?.update(refs.date.value);
            },

            //* 提示更新
            message: (data: { start?: Date; end?: Date }) => {
                refs.start.value = data.start;
                refs.end.value = data.end;
            },

            //* 模式切换
            switchover: (mode: string) => {
                //* 数据更新
                if (mode == "auto") refs.mode.value = define.mode;
                else {
                    refs.mode.value = mode;
                }

                nextTick(() => {
                    //* 组件初始化
                    if (refs.mode.value == "time") {
                        nodes.components.value?.init({ end: refs.end.value, date: refs.date.value, start: refs.start.value });
                    } else {
                        nodes.components.value?.init({ end: define.modelValue.end, date: refs.date.value, start: define.modelValue.start });
                    }

                    //* 位置更新
                    nextTick(() => {
                        //* 检测是否满足运行条件
                        if (!nodes.container.value || !nodes.candidates.value) return;
                        else {
                            //* 隐藏事件
                            window.addEventListener("click", methods.hidden, { capture: true, once: false });
                            //* 数据初始化
                            const data = { container: nodes.container.value, view: nodes.candidates.value };
                            dispose.boundary.relativeContainerBody(data, {
                                direction: "bottom",
                                offset: 8,
                                align: "center",
                            });
                        }
                    });
                });
            },
        },
    };

    return { ons, vars, refs, nodes, binds, watchs, methods, computeds };
};
